import React, { useCallback, useMemo, useRef } from 'react';
import Createable from 'react-select/creatable';
import { components as selectComponents } from 'react-select';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

const getCustomStyles = (hasError, backgroundColor = '#FFFFFF') => ({
  control: (provided) => ({
    ...provided,
    minHeight: '28px', // h-7 equivalent - EXACT match
    height: 'auto',
    border: hasError
      ? '1px solid #ef4444' // border-red-500
      : '1px solid #E0D9CC', // border-edge - EXACT match
    backgroundColor,
    borderRadius: '6px', // rounded-md - EXACT match
    boxShadow: 'none',
    fontSize: '14px', // text-sm - EXACT match
    lineHeight: '1.25', // leading-tight
    '&:hover': {
      border: hasError ? '1px solid #ef4444' : '1px solid #E0D9CC',
      boxShadow: 'none',
    },
    cursor: 'text',
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '2px 8px',
    minHeight: '26px',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '2px',
  }),
  input: (provided) => ({
    ...provided,
    margin: '0',
    padding: '0',
    fontSize: '14px',
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    alignSelf: 'center',
    padding: '0',
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    padding: '0 10px 0 0', // Right padding for proper spacing
    color: '#6B6458', // text-ink-muted - EXACT match with StyledDropdown
    svg: {
      width: '16px', // h-4 w-4 equivalent - EXACT match
      height: '16px',
    },
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#9A9285', // text-ink-muted
    fontSize: '14px',
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: '#E8E4DC', // bg-paper-inset
    borderRadius: '2px',
    fontSize: '12px',
    margin: '0 2px 0 0',
    maxHeight: '22px',
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: '#6B6458', // text-ink-secondary
    fontSize: '12px',
    padding: '0 4px',
    lineHeight: '22px',
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: '#6B6458', // text-ink-muted
    padding: '0 2px',
    '&:hover': {
      backgroundColor: '#E0D9CC',
      color: '#3A3429',
    },
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 1000,
  }),
  menu: (provided) => ({
    ...provided,
    fontSize: '14px',
    borderRadius: '6px',
    border: '1px solid #E0D9CC',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    zIndex: 50,
    marginTop: '2px',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? '#E8E4DC' // bg-paper-inset - lighter to match single select
      : state.isFocused
      ? '#F0ECE4' // bg-paper-muted
      : 'white',
    color: '#3A3429', // text-ink - darker for better readability
    fontSize: '14px',
    padding: '6px 12px',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: state.isSelected ? '#E0D9CC' : '#F0ECE4',
    },
  }),
});

function arrayMove(array, from, to) {
  const next = array.slice();
  next.splice(to < 0 ? next.length + to : to, 0, next.splice(from, 1)[0]);
  return next;
}

/** Renders react-select ValueContainer with Droppable/Draggable (no react-sortable-hoc / findDOMNode). */
function SortableValueContainer(props) {
  const { children, selectProps } = props;
  const value = selectProps.value || [];
  const childrenArray = React.Children.toArray(children);
  // Only the first value.length children are multi-value tags; the rest
  // (Placeholder, Input, etc.) must stay outside Draggable wrappers so
  // clicks on the placeholder still focus the input.
  const tagEls = childrenArray.slice(0, value.length);
  const rest = childrenArray.slice(value.length);

  return (
    <selectComponents.ValueContainer {...props}>
      <Droppable droppableId="styled-multi-select-tags" direction="horizontal">
        {(droppableProvided) => (
          <div
            ref={droppableProvided.innerRef}
            {...droppableProvided.droppableProps}
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 2,
              flex: 1,
              minWidth: 0,
            }}
          >
            {tagEls.map((child, index) => (
              <Draggable
                key={`${String(value[index]?.value ?? '')}-${index}`}
                draggableId={`tag-${String(value[index]?.value ?? index)}`}
                index={index}
              >
                {(dragProvided) => (
                  <div
                    ref={dragProvided.innerRef}
                    {...dragProvided.draggableProps}
                    {...dragProvided.dragHandleProps}
                    role="button"
                    tabIndex={0}
                    aria-label={`Reorder ${value[index]?.label || value[index]?.value || 'selected option'}`}
                    onMouseDown={(e) => {
                      dragProvided.dragHandleProps?.onMouseDown?.(e);
                      e.stopPropagation();
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                      }
                    }}
                  >
                    {child}
                  </div>
                )}
              </Draggable>
            ))}
            {droppableProvided.placeholder}
            {rest}
          </div>
        )}
      </Droppable>
    </selectComponents.ValueContainer>
  );
}

export default function StyledMultiSelectDropdown({
  options = [],
  items = [],
  setItems,
  placeholder = 'Select options...',
  disabled = false,
  backgroundColor = '#F5F3ED',
  className = '',
}) {
  const hasError = className.includes('border-red-500');
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const handleChange = (newValue) => {
    if (setItems) {
      setItems(newValue ? newValue.map((item) => item.value) : []);
    }
  };

  const onDragEnd = useCallback(
    (result) => {
      if (!result.destination) return;
      if (result.source.index === result.destination.index) return;
      const current = itemsRef.current;
      const newValue = arrayMove([...current], result.source.index, result.destination.index);
      if (setItems) setItems(newValue);
    },
    [setItems],
  );

  const selectedOptions = items.map((item) => {
    const option = options.find((opt) => opt.value === item);
    return option || { value: item, label: item };
  });

  const componentsConfig = useMemo(
    () => ({
      ValueContainer: SortableValueContainer,
    }),
    [],
  );

  return (
    <div>
      <DragDropContext onDragEnd={onDragEnd}>
        <Createable
          isMulti
          isClearable={false}
          isSearchable
          options={options}
          value={selectedOptions}
          onChange={handleChange}
          components={componentsConfig}
          menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
          menuPosition="fixed"
          closeMenuOnSelect={false}
          styles={getCustomStyles(hasError, backgroundColor)}
          placeholder={placeholder}
          isDisabled={disabled}
        />
      </DragDropContext>
    </div>
  );
}
